import { defineStore, storeToRefs } from "pinia";
import { ref, computed } from "vue";
import useMyFetch from "@stores/useMyFetch";
import { deepCopy } from "@utils/toolBox";
import { getClientId } from "@utils/clientId";
import { getRegionOptions } from "@/utils/constants/regions";
import { useSurveyTargetsStore } from "@stores/survey/surveyTargets";
import { captureEvent } from "@/lib/analytics";

export const useSurveyProfilesStore = defineStore("surveyProfiles", () => {
  // stores
  const surveyTargetsStore = useSurveyTargetsStore();
  const { currentTargets } = storeToRefs(surveyTargetsStore);

  // state
  const surveyProfiles = ref([]);
  const initialProfiles = ref([]);
  const selectedProfile = ref({});
  const selectedProfileIndex = ref(0);
  const launchErrorMessage = ref("");
  const programmingCheck = ref(null);
  const checking = ref(false);
  const live = ref(false);
  let refreshTimer;
  let refreshCount = 0;

  //getters
  const noProfileExist = computed(() => surveyProfiles.value.length === 0);
  const hasProfilesErrors = computed(() => duplicateExists() || surveyProfiles.value.some((p) => p.errors?.length > 0));

  const hasProfilesChanged = computed(() => {
    const editableFields = [
      "name",
      "country",
      "language",
      "description",
      "n_target",
      "question_params",
      "url_params",
      "active",
    ];
    const getFields = (profiles) => profiles?.map((x) => editableFields.map((field) => x?.[field]));

    return JSON.stringify(getFields(initialProfiles.value)) !== JSON.stringify(getFields(surveyProfiles.value));
  });

  const locked = computed(() => checking.value || programmingCheck.value?.status === "running");

  const countryOptions = computed(() => {
    const regions = getRegionOptions();
    const targets = currentTargets.value;
    const countries = new Set();

    targets.forEach((target) =>
      regions
        .filter((region) => target.countries.includes(region.value))
        .forEach((region) => countries.add(region.value)),
    );

    return Array.from(countries)
      .map((country) => {
        const region = regions.find((region) => region.value === country);
        return { label: region.label, value: region.value };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  });

  // actions
  const getProfiles = (surveyToken) =>
    useMyFetch(`/fake_respondent_profiles/survey_profiles?survey_token=${surveyToken}`).json();

  function setProfiles(profiles) {
    surveyProfiles.value = profiles;
    // DeepCopy breaks the variable's reactivity
    // We'll then have the initial value of the profiles
    initialProfiles.value = deepCopy(profiles);
    selectedProfileIndex.value = 0;
    selectedProfile.value = surveyProfiles.value?.at(0) || {};
    launchErrorMessage.value = "";
  }

  async function fetch(surveyToken) {
    const { data } = await getProfiles(surveyToken);
    setProfiles(data.value);
    return surveyProfiles.value;
  }

  async function save() {
    await Promise.all(surveyProfiles.value.map(async (p) => await fetchPut(p)));
  }

  async function fetchPut(profile) {
    const { data, response } = await useMyFetch(`/fake_respondent_profiles/${profile.token}`)
      .put({ profile: profile })
      .json();
    if (response.value.ok) {
      const index = surveyProfiles.value.findIndex((x) => x.token === profile.token);
      surveyProfiles.value[index] = data.value;

      const copy = deepCopy(data.value);
      const initIndex = initialProfiles.value.findIndex((x) => x.token === profile.token);
      if (initIndex >= 0) initialProfiles.value[initIndex] = copy;
      else initialProfiles.value.push(copy);
    }
    return response.value.ok;
  }

  async function launch(index) {
    launchErrorMessage.value = "";
    const profileToken = surveyProfiles.value[index].token;
    const { data, response } = await useMyFetch(`/fake_respondent_profiles/${profileToken}/launch`)
      .patch({ profile: surveyProfiles.value[index], client_id: getClientId() })
      .json();

    if (response.value.ok) captureEvent("fake_respondents_launched", { profile_token: profileToken });

    surveyProfiles.value[index] = data.value;
    initialProfiles.value[index] = deepCopy(data.value);
    return surveyProfiles.value[index];
  }

  async function refresh(surveyToken) {
    clearTimeout(refreshTimer);
    const count = ++refreshCount;
    const [profiles, check] = await Promise.all([
      getProfiles(surveyToken),
      useMyFetch(`/surveys/${surveyToken}/programming_check`).json(),
    ]);
    if (count !== refreshCount) return;

    if (!hasProfilesChanged.value) setProfiles(profiles.data.value);
    programmingCheck.value = { ...check.data.value, surveyToken };
    if (check.data.value?.status === "running")
      refreshTimer = setTimeout(() => refresh(surveyToken), live.value ? 15000 : 3000);
  }

  const stopRefresh = () => {
    clearTimeout(refreshTimer);
    refreshCount++;
    live.value = false;
  };

  async function checkProgramming(surveyToken, kinds) {
    checking.value = true;
    const { response } = await useMyFetch(`/surveys/${surveyToken}/programming_check`).post({ kinds }).json();
    if (response.value.ok) {
      captureEvent("programming_check_launched", {});
      await refresh(surveyToken);
    }
    checking.value = false;
  }

  async function cancelRun(surveyToken) {
    const { response } = await useMyFetch(`/surveys/${surveyToken}/programming_check`).delete().json();
    if (!response.value.ok) return;

    captureEvent("fake_respondents_cancelled", {});
    await refresh(surveyToken);
  }

  const runAll = async (surveyToken) => {
    checking.value = true;
    const { response } = await useMyFetch(`/surveys/${surveyToken}/programming_check`).patch().json();
    if (response.value.ok) captureEvent("fake_respondents_relaunched", {});
    await refresh(surveyToken);
    checking.value = false;
  };

  async function saveProfile(index) {
    const { token } = surveyProfiles.value[index];
    if (await fetchPut(surveyProfiles.value[index]))
      captureEvent("fake_respondent_profile_saved", { profile_token: token });
    selectedProfile.value = surveyProfiles.value[index];
  }

  async function deleteProfile(index) {
    const profile = surveyProfiles.value[index];
    if (initialProfiles.value.some((p) => p.token === profile.token)) {
      const { response } = await useMyFetch(`/fake_respondent_profiles/${profile.token}`).delete().json();
      if (!response.value.ok && response.value.status !== 404) return;

      captureEvent("fake_respondent_profile_deleted", { profile_token: profile.token });
    }
    surveyProfiles.value.splice(index, 1);
    initialProfiles.value = initialProfiles.value.filter((p) => p.token !== profile.token);
  }

  const discardChanges = () => {
    surveyProfiles.value = deepCopy(initialProfiles.value);
  };

  const duplicateExists = () => {
    const names = surveyProfiles.value.map((p) => p.name.toLowerCase());
    return new Set(names).size !== names.length;
  };

  // profiles channel handlers
  const profilesChannelHandlers = (cable, surveyToken) => {
    let reconnecting = false;
    return {
      connected: () => {
        live.value = true;
        subscribeToChannel(cable, surveyToken)();
        if (reconnecting) refresh(surveyToken);
        reconnecting = true;
      },
      disconnected: () => (live.value = false),
      rejected: () => console.log("rejected"),
      received: handleReceivedMessage(surveyToken),
    };
  };

  const subscribeToChannel = (cable, surveyToken) => () =>
    cable.perform({
      channel: "ProfilesStatusChannel",
      action: "subscribe",
      data: { survey_token: surveyToken },
    });

  const handleReceivedMessage = (surveyToken) => (msg) => {
    const profile = surveyProfiles.value.find((p) => p.token === msg.token);
    if (profile && msg.n_current != null) profile.n_current = msg.n_current;
    if (!profile || msg.ongoing_creation === false)
      refresh(surveyToken).then(() => {
        if ("errors" in msg)
          launchErrorMessage.value = msg.errors?.find((error) => error.name === "launch")?.message ?? "";
      });
  };

  return {
    //state
    surveyProfiles,
    initialProfiles,
    selectedProfile,
    selectedProfileIndex,
    launchErrorMessage,
    programmingCheck,
    checking,
    //getters
    noProfileExist,
    hasProfilesErrors,
    hasProfilesChanged,
    locked,
    countryOptions,
    //actions
    fetch,
    save,
    launch,
    checkProgramming,
    refresh,
    stopRefresh,
    cancelRun,
    runAll,
    saveProfile,
    deleteProfile,
    discardChanges,
    profilesChannelHandlers,
  };
});
